import { useRef } from "react";
import Modal from "@/common/elements/Modal";
import LinkedScannedDocumentsForm from "./LinkedScannedDocumentsForm";
import dayjs from "dayjs";
import notificationService from "@/util/notificationService";

const DocumentsDetailsModal = ({
  show,
  onHide,
  documentFileType,
  documentMode,
  data,
  editingIndex,
  documents = [],
  onChange,
  bookingsFieldsEnabled,
  idFieldName = "warrantLinkedDocumentID",
  parentIdFieldName = "warrantID",
  parentId,
  isSearchMode = false,
}) => {
  const formRef = useRef(null);
  // The list/table only spreads display fields onto each row (see index.jsx's
  // tableRows map) and tucks the real record under _raw — read IDs from there
  // so editing an existing row updates it instead of re-adding it as new.
  const raw = data?._raw ?? data;

  const onSaveClick = async () => {
    try {
      await formRef.current?.validateFields();
    } catch {
      notificationService.error("Please fill in all required fields");
      return;
    }

    const values = formRef.current?.getFormValues();
    if (!values) return;

    const isPhoto  = documentFileType === "Scanned Photo";
    const isLinked = documentFileType === "Linked Documents";
    const existing = documents ?? [];

    const documentDate = values.documentDate
      ? dayjs(values.documentDate).format("MM/DD/YYYY")
      : null;

    const sharedFields = {
      documentName:  values.documentName ?? null,
      documentDate,
      author:        values.author       ?? null,
      comments:      values.comments     ?? null,
      documentType:  values.documentType ?? null,
    };

    let docList = [];
    let photos  = [];

    if (isLinked) {
      // ✅ "Yes" — Linked Document: only documents[], photos stays empty
      docList = [
        {
          ...sharedFields,
          isPhoto:      false,
          documentPath: values.documentPath ?? null,
        },
      ];
      photos = [];
    } else if (isPhoto) {
      // ✅ "No" — Scanned Photo: only photos[], documents stays empty
      docList = [];
      photos = [
        {
          ...sharedFields,
          description:  values.description  ?? null,
          fullSizePath: values.fullSizePath ?? null,
        },
      ];
    }

    const updatedDoc = {
      [idFieldName]:       raw?.[idFieldName]      ?? 0,
      [parentIdFieldName]: raw?.[parentIdFieldName] ?? parentId ?? 0,
      documentID:          isLinked ? (raw?.documentID ?? 0) : 0,
      sequence:            raw?.sequence ?? 0,
      photoID:             isPhoto  ? (raw?.photoID    ?? 0) : 0,
      documents: docList,
      photos,

      // for table display
      _display: {
        documentFileType,
        documentType:  values.documentType ?? "",
        documentName:  values.documentName ?? "",
        documentDate,
        author:        values.author       ?? "",
        comments:      values.comments     ?? "",
        documentPath:  values.documentPath ?? "",
      },
    };

    let updated;
    if (documentMode === "edit" && editingIndex !== null && editingIndex !== undefined) {
      updated = existing.map((d, i) => i === editingIndex ? updatedDoc : d);
    } else {
      updated = [...existing, updatedDoc];
    }

    onChange?.(updated);
    notificationService.success("Document saved");
    onHide();
  };

  const onModalHide = () => {
    formRef.current?.resetForm();
    onHide();
  };

  return (
    <Modal
      header={documentMode === "edit" ? "Edit Document" : "Add Document"}
      show={show}
      onHide={onModalHide}
      size="xl"
      footerActions={[
        {
          key: "save",
          text: "Save",
          onClick: onSaveClick,
          disabled: !bookingsFieldsEnabled,
        },
      ]}
      footerCloseAction
      footerCloseActionText="Close"
    >
      <LinkedScannedDocumentsForm
        ref={formRef}
        document={raw}
        documentMode={documentMode}
        documentFileType={documentFileType}
        bookingsFieldsEnabled={bookingsFieldsEnabled}
        isSearchMode={isSearchMode}
      />
    </Modal>
  );
};

export default DocumentsDetailsModal;